import { type APIComponentAttributes, APIEntityType } from '@ardoq/api-types';
import { dispatchAction } from '@ardoq/rxbeach';
import { combineLatest, map, type Observable } from 'rxjs';
import { context$ } from 'streams/context/context$';
import type { ContextShape, ScenarioModeState } from '@ardoq/data-model';
import {
  Permissions,
  isDisabled,
} from 'streams/currentUserPermissions/permissionInterface';
import { Features, hasFeature } from '@ardoq/features';
import { workspaceAccessControlInterface } from 'resourcePermissions/accessControlHelpers/workspace';
import { currentUserPermissionContext$ } from 'streams/currentUserPermissions/currentUserPermissionContext$';
import {
  type PermissionContext,
  resourcePermissionsOperations,
} from '@ardoq/access-control';
import { componentAccessControlOperation } from 'resourcePermissions/accessControlHelpers/component';
import { getFeatureContext } from 'streams/featureContext$';
import { lockOperations } from 'lockedComponents/lockOperations';
import { componentInterface } from '@ardoq/component-interface';
import {
  type ComponentOptionsCommands,
  type ComponentOptionsProps,
} from './types';
import { trackAuditLogEntryPoint } from 'auditLog/tracking';
import { navigateToAuditLog } from 'router/navigationActions';
import {
  createSurveyFromComponent,
  deleteComponent,
  toggleLockComponent,
} from 'streams/components/ComponentActions';
import { trackEvent } from 'tracking/tracking';
import { editComponent, editComponentStyle } from './actions';
import { type MenuOptionProps } from '@ardoq/sidebar-menu';
import { activeScenario$ } from 'streams/activeScenario/activeScenario$';
import { trackButtonClicked } from 'menus/tracking';
import { activeScenarioOperations } from '@ardoq/core';
import { t, type I18nLocale } from '@ardoq/i18n';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { loadedGraph$ } from 'traversals/loadedGraph$';

const commands: ComponentOptionsCommands = {
  createSurveyFromComponent: componentId => {
    trackEvent('create-survey-from-component-menu-item');
    dispatchAction(createSurveyFromComponent(componentId));
  },
  deleteComponent: componentId => {
    dispatchAction(deleteComponent(componentId));
  },
  editComponentProperties: componentId => {
    dispatchAction(editComponent(componentId));
  },
  editComponentStyle: componentId => {
    dispatchAction(editComponentStyle(componentId));
  },
  toggleLockComponent: componentId => {
    dispatchAction(toggleLockComponent(componentId));
  },
  openAuditLog: (componentId, name) => {
    trackAuditLogEntryPoint('component history');
    dispatchAction(
      navigateToAuditLog({
        entities: [{ id: componentId, name }],
        entityType: APIEntityType.COMPONENT,
      })
    );
  },
};

const getEditComponentOptions = (
  component: APIComponentAttributes,
  data: {
    commands: ComponentOptionsCommands;
    permissionContext: PermissionContext;
    activeScenarioState: ScenarioModeState | null;
    i18nLocale: I18nLocale;
    isViewpointMode: boolean;
  }
): MenuOptionProps[] => {
  const {
    commands,
    permissionContext,
    activeScenarioState,
    i18nLocale,
    isViewpointMode,
  } = data;
  const canEditComponentProperties =
    componentAccessControlOperation.canEditComponent({
      component,
      permissionContext,
      featureContext: getFeatureContext(),
    });

  const hasWorkspaceWriteAccess =
    workspaceAccessControlInterface.canEditWorkspace(
      permissionContext,
      component.rootWorkspace,
      activeScenarioState
    );

  if (!canEditComponentProperties) return [];

  const featureContext = getFeatureContext();
  const isLocked = lockOperations.isLocked(component.lock, featureContext);
  const canToggleLock =
    lockOperations.isLockingEnabled(featureContext) &&
    (isLocked
      ? componentAccessControlOperation.canUnlock({
          permissionContext,
          component,
        })
      : resourcePermissionsOperations.canEditWorkspace(
          permissionContext,
          component.rootWorkspace
        ));

  return [
    {
      label: t(
        i18nLocale,
        'menus:componentOptions.editComponentProperties',
        'Edit component properties'
      ),
      onClick: () => {
        trackButtonClicked('edit component properties', isViewpointMode);
        commands.editComponentProperties(component._id);
      },
      isDisabled: isLocked,
      dataClickId: 'edit-component-properties-menu-item',
      isVisible: hasWorkspaceWriteAccess,
    },
    {
      label: t(
        i18nLocale,
        'menus:componentOptions.editComponentStyle',
        'Edit component style'
      ),
      onClick: () => {
        trackButtonClicked('edit component style', isViewpointMode);
        commands.editComponentStyle(component._id);
      },
      isDisabled: isLocked,
      dataClickId: 'edit-component-style-menu-item',
      isVisible: hasWorkspaceWriteAccess,
    },
    {
      label: t(
        i18nLocale,
        'menus:componentOptions.deleteComponent',
        'Delete component'
      ),
      onClick: () => {
        trackButtonClicked('delete component', isViewpointMode);
        commands.deleteComponent(component._id);
      },
      isDisabled: isLocked,
      dataClickId: 'delete-component-menu-item',
      isVisible: hasWorkspaceWriteAccess,
    },
    {
      label: isLocked
        ? t(
            i18nLocale,
            'menus:componentOptions.unlockComponent',
            'Unlock component'
          )
        : t(
            i18nLocale,
            'menus:componentOptions.lockComponent',
            'Lock component'
          ),
      onClick: () => {
        trackButtonClicked('toggle lock component', isViewpointMode);
        commands.toggleLockComponent(component._id);
      },
      dataClickId: 'toggle-lock-component-menu-item',
      isVisible: canToggleLock,
    },
  ].filter(option => option.isVisible);
};

const getActionOptions = (
  component: APIComponentAttributes,
  data: {
    commands: ComponentOptionsCommands;
    context: ContextShape;
    permissionContext: PermissionContext;
    activeScenarioState: ScenarioModeState | null;
    i18nLocale: I18nLocale;
  }
): MenuOptionProps[] => {
  const {
    commands,
    context,
    permissionContext,
    activeScenarioState,
    i18nLocale,
  } = data;
  const hasWorkspaceWriteAccess =
    workspaceAccessControlInterface.canEditWorkspace(
      permissionContext,
      context.workspaceId,
      activeScenarioState
    );

  if (!hasWorkspaceWriteAccess) return [];

  const canCreateSurvey = hasFeature(Features.SURVEYS);
  const isViewVersionHistoryDisabled = isDisabled(
    Permissions.COMPONENT_VIEW_HISTORY
  );

  const isCreateSurveyFromComponentDisabled =
    !workspaceAccessControlInterface.canAdminWorkspace(
      permissionContext,
      context.workspaceId,
      activeScenarioState
    ) || isDisabled(Permissions.SURVEY_CREATE_FROM_COMPONENT);

  return [
    {
      label: t(
        i18nLocale,
        'menus:componentOptions.viewComponentHistory',
        'View component history'
      ),
      onClick: () => commands.openAuditLog(component._id, component.name),
      isDisabled: isViewVersionHistoryDisabled,
      dataClickId: 'view-component-history-menu-item',
      isVisible: true,
    },
    {
      label: t(
        i18nLocale,
        'menus:componentOptions.createSurveyFromComponent',
        'Create survey from component'
      ),
      onClick: () => commands.createSurveyFromComponent(component._id),
      isDisabled: isCreateSurveyFromComponentDisabled,
      dataClickId: 'create-survey-from-component-menu-item',
      isVisible: canCreateSurvey,
    },
  ].filter(option => option.isVisible);
};

type StreamDependencies = {
  context: ContextShape;
  permissionContext: PermissionContext;
  activeScenarioState: ScenarioModeState;
  i18nLocale: I18nLocale;
  isViewpointMode: boolean;
};

const toComponentOptionsViewModel = ({
  context,
  permissionContext,
  activeScenarioState,
  i18nLocale,
  isViewpointMode,
}: StreamDependencies): ComponentOptionsProps => {
  const isScenarioMode =
    activeScenarioOperations.isInScenarioMode(activeScenarioState);

  // This is a side effect, but we can't avoid it. because the components$ stream is not reliable.
  const component = componentInterface.get(context.componentId);

  if (!component) {
    return {
      mode: 'error',
      message: t(
        i18nLocale,
        'menus:componentOptions.error.selectComponent',
        'Please select a component'
      ),
    };
  }

  if (
    isScenarioMode &&
    componentInterface.isScenarioContextComponent(component._id)
  ) {
    return {
      mode: 'error',
      message: t(
        i18nLocale,
        'menus:componentOptions.error.notInScenario',
        'Components that are not part of the scenario cannot be edited.'
      ),
    };
  }

  const canEditComponentProperties =
    componentAccessControlOperation.canEditComponent({
      component,
      permissionContext,
      featureContext: getFeatureContext(),
    });
  if (!canEditComponentProperties) {
    return {
      mode: 'error',
      message: t(
        i18nLocale,
        'menus:componentOptions.error.noEditAccess',
        "You don't have edit access to the component."
      ),
    };
  }

  return {
    mode: 'ok',
    actionOptions: getActionOptions(component, {
      commands,
      context,
      permissionContext,
      activeScenarioState,
      i18nLocale,
    }),
    editComponentOptions: getEditComponentOptions(component, {
      commands,
      permissionContext,
      activeScenarioState,
      i18nLocale,
      isViewpointMode,
    }),
    editComponentSectionTitle: t(
      i18nLocale,
      'menus:componentOptions.section.editComponent',
      'Edit component'
    ),
    actionsSectionTitle: t(
      i18nLocale,
      'menus:componentOptions.section.actions',
      'Actions'
    ),
  };
};
export const componentOptions$: Observable<ComponentOptionsProps> =
  combineLatest({
    context: context$,
    permissionContext: currentUserPermissionContext$,
    activeScenarioState: activeScenario$,
    i18nLocale: i18nLocale$,
    loadedGraph: loadedGraph$,
  }).pipe(
    map(({ loadedGraph, ...rest }) =>
      toComponentOptionsViewModel({
        ...rest,
        isViewpointMode: loadedGraph.isViewpointMode,
      })
    )
  );
